import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  Checkbox,
  Toolbar,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  SelectAll as SelectAllIcon,
  ClearAll as ClearAllIcon
} from '@mui/icons-material';
import lockUnlockApi from '../../services/lockUnlockApi';
import { formatDate } from '../../utils/dateUtils';
import notificationService from '../../services/notificationService';

const AdminUnlockRequestsPanel = () => {
  // State
  const [unlockRequests, setUnlockRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedRequests, setSelectedRequests] = useState([]); // For bulk operations
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showBulkApproveDialog, setShowBulkApproveDialog] = useState(false);
  const [showBulkRejectDialog, setShowBulkRejectDialog] = useState(false);
  const [adminNote, setAdminNote] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // Fetch unlock requests on component mount
  useEffect(() => {
    fetchUnlockRequests();
    
    // Trigger notification service check after successful actions
    const handleRefreshFromNotifications = () => {
      console.log('🔔 Real-time notification received, refreshing unlock requests');
      fetchUnlockRequests();
    };
    
    // Subscribe to notification updates for real-time refresh
    const unsubscribe = notificationService.subscribe(handleRefreshFromNotifications);
    
    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Close snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Fetch all unlock requests
  const fetchUnlockRequests = async () => {
    setLoading(true);
    try {
      const response = await lockUnlockApi.getUnlockRequests();
      setUnlockRequests(response.data);
    } catch (error) {
      console.error('Error fetching unlock requests:', error);
      showSnackbar('Failed to load unlock requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve request
  const handleApproveRequest = async () => {
    if (!selectedRequest) return;
    
    setLoading(true);
    try {
      await lockUnlockApi.approveUnlockRequest(selectedRequest.id, adminNote);
      showSnackbar('Request approved successfully');
      setShowApproveDialog(false);
      fetchUnlockRequests(); // Refresh the list
      // Trigger notification service check for real-time updates
      notificationService.checkForUpdates();
    } catch (error) {
      console.error('Error approving request:', error);
      showSnackbar('Failed to approve request: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Handle reject request
  const handleRejectRequest = async () => {
    if (!selectedRequest) return;
    
    setLoading(true);
    try {
      await lockUnlockApi.rejectUnlockRequest(selectedRequest.id, adminNote);
      showSnackbar('Request rejected successfully');
      setShowRejectDialog(false);
      fetchUnlockRequests(); // Refresh the list
      // Trigger notification service check for real-time updates
      notificationService.checkForUpdates();
    } catch (error) {
      console.error('Error rejecting request:', error);
      showSnackbar('Failed to reject request: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Open approve dialog
  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setAdminNote('');
    setShowApproveDialog(true);
  };

  // Open reject dialog
  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setAdminNote('');
    setShowRejectDialog(true);
  };

  // Bulk selection handlers
  const handleSelectAll = () => {
    const pendingRequests = filteredRequests.filter(req => req.status === 'pending');
    setSelectedRequests(pendingRequests.map(req => req.id));
  };

  const handleClearSelection = () => {
    setSelectedRequests([]);
  };

  const handleSelectRequest = (requestId) => {
    setSelectedRequests(prev => 
      prev.includes(requestId) 
        ? prev.filter(id => id !== requestId)
        : [...prev, requestId]
    );
  };

  // Bulk approve requests
  const handleBulkApprove = async () => {
    if (selectedRequests.length === 0) return;
    
    setLoading(true);
    try {
      await lockUnlockApi.bulkApproveUnlockRequests(selectedRequests, adminNote);
      showSnackbar(`Successfully approved ${selectedRequests.length} requests`);
      setShowBulkApproveDialog(false);
      setSelectedRequests([]);
      fetchUnlockRequests();
      // Trigger notification service check for real-time updates
      notificationService.checkForUpdates();
    } catch (error) {
      console.error('Error bulk approving requests:', error);
      showSnackbar('Failed to bulk approve requests: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Bulk reject requests
  const handleBulkReject = async () => {
    if (selectedRequests.length === 0) return;
    
    setLoading(true);
    try {
      await lockUnlockApi.bulkRejectUnlockRequests(selectedRequests, adminNote);
      showSnackbar(`Successfully rejected ${selectedRequests.length} requests`);
      setShowBulkRejectDialog(false);
      setSelectedRequests([]);
      fetchUnlockRequests();
      // Trigger notification service check for real-time updates
      notificationService.checkForUpdates();
    } catch (error) {
      console.error('Error bulk rejecting requests:', error);
      showSnackbar('Failed to bulk reject requests: ' + (error.message || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  // Filter requests based on status
  const filteredRequests = unlockRequests.filter(request => 
    statusFilter === 'all' || request.status === statusFilter
  );

  // Render status chip
  const renderStatusChip = (status) => {
    switch (status) {
      case 'pending':
        return <Chip label="Pending" color="warning" />;
      case 'approved':
        return <Chip label="Approved" color="success" />;
      case 'rejected':
        return <Chip label="Rejected" color="error" />;
      default:
        return <Chip label={status} />;
    }
  };

  return (
    <>
      {/* Controls */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Manage all unlock requests from across the system
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            onClick={fetchUnlockRequests}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* Bulk Actions Toolbar */}
      {filteredRequests.some(req => req.status === 'pending') && (
        <Toolbar 
          sx={{ 
            pl: { sm: 2 }, 
            pr: { xs: 1, sm: 1 },
            bgcolor: selectedRequests.length > 0 ? 'action.selected' : 'transparent',
            borderRadius: 1,
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <Tooltip title="Select all pending requests">
              <IconButton onClick={handleSelectAll}>
                <SelectAllIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Clear selection">
              <IconButton onClick={handleClearSelection}>
                <ClearAllIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="subtitle1">
              {selectedRequests.length > 0 
                ? `${selectedRequests.length} selected` 
                : 'Select requests for bulk actions'
              }
            </Typography>
          </Box>
          
          {selectedRequests.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setShowBulkApproveDialog(true)}
                disabled={loading}
              >
                Bulk Approve ({selectedRequests.length})
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setShowBulkRejectDialog(true)}
                disabled={loading}
              >
                Bulk Reject ({selectedRequests.length})
              </Button>
            </Box>
          )}
        </Toolbar>
      )}

      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}

      {!loading && filteredRequests.length === 0 && (
        <Alert severity="info" sx={{ my: 2 }}>
          {statusFilter === 'all' ? 'No unlock requests found.' : `No ${statusFilter} unlock requests found.`}
        </Alert>
      )}

      {!loading && filteredRequests.length > 0 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedRequests.length > 0 && selectedRequests.length < filteredRequests.filter(req => req.status === 'pending').length}
                    checked={filteredRequests.filter(req => req.status === 'pending').length > 0 && selectedRequests.length === filteredRequests.filter(req => req.status === 'pending').length}
                    onChange={(e) => e.target.checked ? handleSelectAll() : handleClearSelection()}
                  />
                </TableCell>
                <TableCell>Request ID</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Row ID</TableCell>
                <TableCell>Requested By</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id} selected={selectedRequests.includes(request.id)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedRequests.includes(request.id)}
                      onChange={() => handleSelectRequest(request.id)}
                      disabled={request.status !== 'pending'}
                    />
                  </TableCell>
                  <TableCell>{request.id}</TableCell>
                  <TableCell>{request.campaign_name}</TableCell>
                  <TableCell>{request.accrual_row}</TableCell>
                  <TableCell>{request.requested_by_username || 'Unknown'}</TableCell>
                  <TableCell>{formatDate(request.created_at)}</TableCell>
                  <TableCell>{request.note}</TableCell>
                  <TableCell>{renderStatusChip(request.status)}</TableCell>
                  <TableCell>
                    {request.status === 'pending' && (
                      <Box>
                        <Button 
                          size="small" 
                          color="primary" 
                          onClick={() => openApproveDialog(request)}
                          sx={{ mr: 1 }}
                        >
                          Approve
                        </Button>
                        <Button 
                          size="small" 
                          color="error" 
                          onClick={() => openRejectDialog(request)}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                    {request.status !== 'pending' && (
                      <Typography variant="body2" color="textSecondary">
                        {request.admin_note ? `Note: ${request.admin_note}` : 'No admin note'}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialogs and Snackbar */}
      <Dialog open={showApproveDialog} onClose={() => setShowApproveDialog(false)}>
        <DialogTitle>Approve Unlock Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve this unlock request? This will unlock the row and allow the user to edit it.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Note (Optional)"
            fullWidth
            variant="outlined"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApproveDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleApproveRequest} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showRejectDialog} onClose={() => setShowRejectDialog(false)}>
        <DialogTitle>Reject Unlock Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject this unlock request? Please provide a reason for rejection.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            variant="outlined"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRejectDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleRejectRequest} 
            color="error" 
            disabled={loading || !adminNote.trim()}
          >
            {loading ? <CircularProgress size={24} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showBulkApproveDialog} onClose={() => setShowBulkApproveDialog(false)}>
        <DialogTitle>Bulk Approve Unlock Requests</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to approve {selectedRequests.length} unlock requests? 
            This will unlock the selected rows and allow users to edit them.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Admin Note (Optional)"
            fullWidth
            variant="outlined"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkApproveDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleBulkApprove} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : `Approve ${selectedRequests.length} Requests`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showBulkRejectDialog} onClose={() => setShowBulkRejectDialog(false)}>
        <DialogTitle>Bulk Reject Unlock Requests</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject {selectedRequests.length} unlock requests? 
            Please provide a reason for rejection.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Rejection Reason"
            fullWidth
            variant="outlined"
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            required
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkRejectDialog(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleBulkReject} 
            color="error" 
            disabled={loading || !adminNote.trim()}
          >
            {loading ? <CircularProgress size={24} /> : `Reject ${selectedRequests.length} Requests`}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AdminUnlockRequestsPanel; 